import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Lock, Users, Globe, UserCheck, UserPlus, Eye, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PrivacySelectorProps {
  value: string;
  onChange: (value: string) => void;
  isCreatingForOtherUser?: boolean;
  timelineOwnerName?: string;
  isOwnTimeline?: boolean;
}

const PrivacySelector = ({ 
  value, 
  onChange, 
  isCreatingForOtherUser = false,
  timelineOwnerName,
  isOwnTimeline = true
}: PrivacySelectorProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const { t } = useTranslation('timeline');

  const getPrivacyOptions = () => {
    const ownerName = timelineOwnerName || 'Timeline Owner';
    
    const allOptions = [
      {
        value: 'creator_and_owner',
        label: isOwnTimeline ? t('privacy.justMe') : t('privacy.meAndOwner', { name: ownerName }),
        icon: Lock,
        description: 'Only you and the timeline owner can see this',
        color: 'bg-gray-100 text-gray-700',
        requiresApproval: false
      },
      {
        value: 'mutual_friends',
        label: t('privacy.mutualFriends'),
        icon: UserCheck,
        description: 'Visible to mutual friends of you and the timeline owner',
        color: 'bg-blue-100 text-blue-700',
        requiresApproval: isCreatingForOtherUser
      },
      {
        value: 'timeline_access',
        label: isOwnTimeline ? t('privacy.timelineAccess') : t('privacy.everyoneOnTimeline', { name: ownerName }),
        icon: Eye,
        description: 'Visible to anyone who can access this timeline',
        color: 'bg-green-100 text-green-700',
        requiresApproval: isCreatingForOtherUser
      },
      {
        value: 'friends',
        label: t('privacy.friends'),
        icon: Users,
        description: 'Visible to all friends (legacy option)',
        color: 'bg-purple-100 text-purple-700',
        requiresApproval: isCreatingForOtherUser
      },
      {
        value: 'public',
        label: t('privacy.public'),
        icon: Globe,
        description: 'Visible to everyone (legacy option)',
        color: 'bg-red-100 text-red-700',
        requiresApproval: isCreatingForOtherUser
      }
    ];

    // Filter out mutual_friends option when it's the user's own timeline
    return isOwnTimeline 
      ? allOptions.filter(option => option.value !== 'mutual_friends')
      : allOptions;
  };

  const privacyOptions = getPrivacyOptions();
  const selectedOption = privacyOptions.find(opt => opt.value === value);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">Privacy Level</label>
        <Dialog open={showInfo} onOpenChange={setShowInfo}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
              <Info className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Privacy Levels Explained</DialogTitle>
              <DialogDescription>
                Choose who can see this memory on the timeline
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {privacyOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <div key={option.value} className="flex items-start gap-3 p-3 rounded-lg border">
                    <Icon className="h-5 w-5 mt-0.5 text-gray-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{option.label}</span>
                        {option.requiresApproval && (
                          <Badge variant="outline" className="text-xs">
                            Requires Approval
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue>
            {selectedOption && (
              <div className="flex items-center gap-2">
                <selectedOption.icon className="h-4 w-4" />
                <span>{selectedOption.label}</span>
                {selectedOption.requiresApproval && (
                  <Badge variant="outline" className="text-xs ml-auto">
                    Requires Approval
                  </Badge>
                )}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {privacyOptions.map((option) => {
            const Icon = option.icon;
            return (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2 w-full">
                  <Icon className="h-4 w-4" />
                  <span>{option.label}</span>
                  {option.requiresApproval && (
                    <Badge variant="outline" className="text-xs ml-auto">
                      Requires Approval
                    </Badge>
                  )}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {selectedOption?.requiresApproval && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <UserPlus className="h-4 w-4 text-amber-600 mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-800 font-medium">Approval Required</p>
            <p className="text-amber-700">
              This memory will be sent to the timeline owner for approval before it becomes visible.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySelector;
